import { useQuery } from '@tanstack/react-query';
import { integrationApiService } from '../../../services/integrationApi';
import { useOrgFeatureFlag } from '../../../hooks/useOrgFeatureFlags';
import { V2_TAXONOMY_QUERY_KEY } from './v2Ui';

/** Same keys the flag-off section uses, so both layouts share one cache. */
export const SUMMARY_QUERY_KEY = ['shopify-category-summary'] as const;
export const MAPPING_INDEX_QUERY_KEY = ['mapping-index'] as const;

/**
 * Everything View A reads besides the layout itself. A flag that is off turns its query off,
 * so the piece it feeds is simply absent.
 */
export function useTypeCategoryData(integrationId: string) {
  const { enabled: ownershipOn } = useOrgFeatureFlag('shopify_field_ownership');
  const { enabled: taxonomyOn } = useOrgFeatureFlag('merchant_taxonomy');
  const { enabled: ruleCategoryOn } = useOrgFeatureFlag('name_rule_shopify_category');
  const { enabled: structuredOn } = useOrgFeatureFlag('structured_mapping_inputs');
  const { enabled: summaryOn } = useOrgFeatureFlag('shopify_mapping_simple_mode');
  const { enabled: unmappedUnsetOn } = useOrgFeatureFlag('shopify_unmapped_category_unset');
  const { enabled: lensFocalRangeOn } = useOrgFeatureFlag('lens_focal_range');

  const categoryDefaults = useQuery({
    queryKey: ['shopify-category-defaults'],
    queryFn: () => integrationApiService.getShopifyCategoryDefaults(),
    staleTime: Infinity,
    retry: false,
  });
  const taxonomy = useQuery({
    queryKey: V2_TAXONOMY_QUERY_KEY,
    queryFn: () => integrationApiService.getMerchantTaxonomy(),
    enabled: taxonomyOn,
    retry: false,
  });
  const mappingIndex = useQuery({
    queryKey: [...MAPPING_INDEX_QUERY_KEY, integrationId],
    queryFn: () => integrationApiService.getMappingIndex(integrationId),
    enabled: structuredOn,
    retry: false,
  });
  const summary = useQuery({
    queryKey: [...SUMMARY_QUERY_KEY, integrationId],
    queryFn: () => integrationApiService.getShopifyCategorySummary(integrationId),
    enabled: summaryOn,
    retry: false,
  });

  return {
    flags: { ownershipOn, taxonomyOn, ruleCategoryOn, structuredOn, summaryOn, unmappedUnsetOn, lensFocalRangeOn },
    categoryDefaults: categoryDefaults.data,
    categoryDefaultsFailed: categoryDefaults.isError,
    taxonomyOptions: categoryDefaults.data?.options ?? [],
    registry: taxonomyOn ? taxonomy.data?.registry ?? null : null,
    taxonomyStats: taxonomy.data?.stats,
    taxonomyLoading: taxonomyOn && taxonomy.isLoading,
    indexRows: structuredOn ? mappingIndex.data?.products : undefined,
    summary: summaryOn ? summary.data : undefined,
    summaryLoading: summaryOn && summary.isLoading,
    summaryError: summaryOn && summary.isError ? summary.error : null,
  };
}

export type TypeCategoryData = ReturnType<typeof useTypeCategoryData>;
